import React, {FC} from 'react';
import styled from 'styled-components';

import {
  DropdownItem,
  DropdownItemProps,
} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AccountModel} from '../../../models/api/accountModel';
import {AccountAvatar} from '../avatars/accountAvatar';

/*
 * Constants.
 */

export const rowHeightWithoutDomains = 30; // in px.
export const rowHeightWithDomains = 46; // in px.

/*
 * Props.
 */

interface AccountDropdownItemProps extends DropdownItemProps {
  account: AccountModel;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: flex;
  align-items: flex-begin;
  justify-content: space-between;
  max-width: 100%;
  max-height: 36px;
`;

const StyledLogoDiv = styled.div`
  width: 20px;
  margin-right: 10px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledDetailsDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  max-width: 200px;
  ${ellipsis()};
`;

interface StyledNameDivProps {
  $isChecked: boolean;
}

const StyledNameDiv = styled.div<StyledNameDivProps>`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${(p) => (p.$isChecked ? fontWeights.bold : fontWeights.medium)};
  height: 16px;
`;

const StyledDomainsDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  height: 16px;
`;

/*
 * Component.
 */

export const AccountDropdownItem: FC<AccountDropdownItemProps> = (props) => {
  const {account, isChecked} = props;
  const domainsString = account.domains.join(', ');

  return (
    <DropdownItem {...props}>
      <DropdownItemContent isChecked={props.isChecked}>
        <StyledContentDiv>
          <StyledLogoDiv>{renderIcon(account)}</StyledLogoDiv>
          <StyledDetailsDiv>
            <StyledNameDiv $isChecked={Boolean(isChecked)}>{account.name}</StyledNameDiv>
            {account.domains.length > 0 && <StyledDomainsDiv>{domainsString}</StyledDomainsDiv>}
          </StyledDetailsDiv>
        </StyledContentDiv>
      </DropdownItemContent>
    </DropdownItem>
  );
};

/*
 * Helpers.
 */

function renderIcon(account: AccountModel) {
  // The default AccountAvatar placeholder is also wrapped in a circle. We want to render a full-sized briefcase.
  const placeholder = (
    <UserThemeConsumer>
      {(theme) => <Icon name="briefcaseFilled" color={theme.greys.shade60} size={20} />}
    </UserThemeConsumer>
  );
  return <AccountAvatar account={account} size={20} avatarIconSize={12} placeholder={placeholder} />;
}
